import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ChatMessage from './ChatMessage';
import MessageInput from './MessageInput';
import SLLogo from './assets/slLogo';
import ConvaiClient from '../services/convaiService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  margin: 0 auto;
  background-color: #f5f7fb;
  overflow: hidden;
`;

const Header = styled.div`
  background-color: #4a6fa5;
  color: white;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px; /* Increased gap between elements */
`;


const HeaderTitle = styled.h1`
  margin: 0 0 0 12px;
  font-size: 1.5rem;
  font-weight: 600;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: #f8fafd;
`;

const TypingIndicator = styled.div`
  padding: 8px 16px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  color: #555;
  width: fit-content;
  margin-top: 8px;
  font-size: 0.9rem;
  align-self: flex-start;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background-color: #4a6fa5;
    border-radius: 50%;
    margin-right: 8px;
    animation: pulse 1.5s infinite;
  }
  
  @keyframes pulse {
    0% { opacity: 0.5; }
    50% { opacity: 1; }
    100% { opacity: 0.5; }
  }
`;


const ChatWindow = () => {
  const [messages, setMessages] = useState([
    { id: 1, text: "Hello! How can I help you today?", sender: "bot", timestamp: new Date() },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const convaiService = useRef(null);
  const nextMessageId = useRef(2);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize Convai service
  const initializeConvaiService = () => {
    if (isInitialized) return; // Prevent multiple initializations
    
    // Create Convai client instance with hardcoded credentials
    const API_KEY = "3ccd91a0-f85c-11ef-99d8-42010a7be01a";
    const CHARACTER_ID = "05f8618c-ee7f-11ef-a457-42010a7be016";
    
    convaiService.current = new ConvaiClient(
      API_KEY,
      CHARACTER_ID
    );

    // Set up message handler
    convaiService.current.onMessage((text) => {
      // Update existing message if bot is already responding
      setMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        // if (lastMessage && lastMessage.sender === "bot" && convaiService.current.isResponding) {
        if (lastMessage && lastMessage.sender === "bot") {
          // Update the last message
          const updatedMessages = [...prev];
          updatedMessages[updatedMessages.length - 1] = {
            ...lastMessage,
            text: lastMessage.text + text,  // Concatenate the new text with existing text
            interactionId: convaiService.current.interactionId
          };
          return updatedMessages;
        }
        
        // Otherwise create a new message
        return [...prev, {
          id: nextMessageId.current++,
          text,
          sender: "bot",
          timestamp: new Date(),
          interactionId: convaiService.current.interactionId
        }];
      });
      setIsTyping(false);
    });

    // Set up error handler
    convaiService.current.onError((errorType, errorMessage, errorCode) => {
      console.error(`Error from Convai service: ${errorType} - ${errorMessage} (${errorCode})`);
      setIsTyping(false);
      
      if (errorType === "chatbotResponseError") {
        const errorResponse = {
          id: nextMessageId.current++,
          text: "Sorry, there was an error communicating with the chat service. Please try again.",
          sender: "bot",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorResponse]);
      }
    });

    setIsInitialized(true);
  };

  const handleStartTyping = () => {
    if (!isInitialized) {
      initializeConvaiService();
    }
  };

  // Fallback response if connection fails
  const generateFallbackResponse = (userMessage) => {
    const lowerMsg = userMessage.toLowerCase();
    
    if (lowerMsg.includes('hello') || lowerMsg.includes('hi') || lowerMsg.includes('hey')) {
      return "Hello there! How can I assist you today?";
    } else if (lowerMsg.includes('how are you')) {
      return "I'm doing great, thanks for asking! How about you?";
    } else if (lowerMsg.includes('help') || lowerMsg.includes('support')) {
      return "I'd be happy to help! What do you need assistance with?";
    } else if (lowerMsg.includes('bye') || lowerMsg.includes('goodbye')) {
      return "Goodbye! Have a wonderful day!";
    } else if (lowerMsg.includes('thank')) {
      return "You're welcome! Is there anything else I can help with?";
    } else {
      return "Thanks for your message! I'm currently in offline mode. Please check your connection to the chat service.";
    }
  };

  const sendMessage = async (text) => {
    // Add user message to chat
    const newMessage = {
      id: nextMessageId.current++,
      text,
      sender: "user",
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, newMessage]);
    setIsTyping(true);
    
    try {
      // Make sure we have a convaiService instance
      if (!convaiService.current) {
        console.log("No Convai service instance, initializing...");
        initializeConvaiService();
      }
      
      // Always try to reconnect before sending
      try {
        const connected = convaiService.current.start();
        console.log("Connection result:", connected);
        
        if (!connected) {
          throw new Error("Failed to connect to service");
        }
      } catch (reconnectError) {
        console.error("Error during connection attempt:", reconnectError);
        throw reconnectError; // Re-throw to be caught by outer catch
      }
      
      // Send message to Convai service
      console.log("Sending message to Convai service...");
      await convaiService.current.sendMessage(text);
      console.log("Message sent successfully");
    } catch (error) {
      console.error("Error processing message:", error);
      setIsTyping(false);
      
      // Use fallback response for any error
      setTimeout(() => {
        const botResponse = {
          id: nextMessageId.current++,
          text: generateFallbackResponse(text),
          sender: "bot",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botResponse]);
      }, 1500);
    }
  };

  // Auto-scroll to the bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <SLLogo width={28} height={28} />
          <HeaderTitle>Second Life Chat</HeaderTitle>
        </HeaderLeft>
        <HeaderRight>
          {/* Header right is empty now that settings are removed */}
        </HeaderRight>
      </Header>
      <MessagesContainer>
        <div style={{ maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
          {messages.map((message) => (
            <ChatMessage 
              key={message.id} 
              message={message} 
              onFeedback={(thumbsUp, feedbackText) => {
                if (convaiService.current && message.interactionId) {
                  // Add logging to debug
                  console.log("Sending feedback for interaction:", message.interactionId);
                  convaiService.current.sendFeedback(
                    thumbsUp, 
                    feedbackText,
                    message.interactionId  // Pass the interactionId
                  );
                } else {
                  console.warn("Cannot send feedback - missing service or interactionId:", {
                    hasService: !!convaiService.current,
                    interactionId: message.interactionId
                  });
                }
              }} 
            />
          ))}
          {isTyping && <TypingIndicator>Typing</TypingIndicator>}
          <div ref={messagesEndRef} />
        </div>
      </MessagesContainer>
      <MessageInput 
        onSendMessage={sendMessage} 
        onStartTyping={handleStartTyping}
      />
    </Container>
  );
};

export default ChatWindow;
